import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  ExternalLink,
  TrendingUp,
  TrendingDown,
  Minus,
  User,
  Clock,
  Globe,
  Tag,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';

const SentimentNews = ({ onLoadMore = null }) => {
  const { t } = useTranslation();
  const [hoveredCard, setHoveredCard] = useState(null);
  const [selectedSentiment, setSelectedSentiment] = useState('all');
  const [showAll, setShowAll] = useState(false);

  // Redux
  const sentimentData = useSelector((state) => state.sentiment?.sentimentData || {});
  const loading = useSelector((state) => state.sentiment.loading);
  const error = useSelector((state) => state.sentiment.error);

  // Helpers
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '—';
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    if (diffInHours < 1) return t('stocks.time.justNow');
    if (diffInHours < 24) return t('stocks.time.hoursAgo', { count: diffInHours });
    if (diffInHours < 48) return t('stocks.time.yesterday');
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  const getMostCommonSentiment = (insights = []) => {
    const sentimentCounts = insights.reduce((acc, insight) => {
      if (!insight?.sentiment) return acc;
      acc[insight.sentiment] = (acc[insight.sentiment] || 0) + 1;
      return acc;
    }, {});
    const entries = Object.entries(sentimentCounts);
    if (entries.length === 0) return 'neutral';
    return entries.reduce((a, b) => (a[1] > b[1] ? a : b))[0];
  };

  const getSentimentConfig = (sentiment) => {
    switch (sentiment) {
      case 'positive':
        return {
          icon: <TrendingUp className="w-4 h-4 text-emerald-700 dark:text-emerald-400" />,
          bgGradient: 'from-emerald-500/10 via-green-500/5 to-transparent',
          borderColor: 'border-emerald-500/30',
          textColor: 'text-emerald-700 dark:text-emerald-400',
          glowColor: 'shadow-emerald-500/20',
          ringColor: 'ring-emerald-400/20',
        };
      case 'negative':
        return {
          icon: <TrendingDown className="w-4 h-4 text-rose-700 dark:text-rose-400" />,
          bgGradient: 'from-rose-500/10 via-red-500/5 to-transparent',
          borderColor: 'border-rose-500/30',
          textColor: 'text-rose-700 dark:text-rose-400',
          glowColor: 'shadow-rose-500/20',
          ringColor: 'ring-rose-400/20',
        };
      default:
        return {
          icon: <Minus className="w-4 h-4 text-amber-700 dark:text-amber-400" />,
          bgGradient: 'from-amber-500/10 via-yellow-500/5 to-transparent',
          borderColor: 'border-amber-500/30',
          textColor: 'text-amber-700 dark:text-amber-400',
          glowColor: 'shadow-amber-500/20',
          ringColor: 'ring-amber-400/20',
        };
    }
  };

  // Normalize input
  const transformedData = useMemo(() => {
    if (!Array.isArray(sentimentData.articles)) return [];
    return sentimentData.articles.map((article) => {
      // keywords can be array or JSON string
      let keywords = [];
      try {
        if (Array.isArray(article.keywords)) keywords = article.keywords;
        else if (typeof article.keywords === 'string') keywords = JSON.parse(article.keywords);
      } catch {
        keywords = [];
      }

      const publisher =
        article.publisher_name ||
        article.publisher?.name ||
        'Unknown Publisher';

      let sentiment = 'neutral';
      if (article.sentiment) sentiment = article.sentiment;
      else if (Array.isArray(article.insights) && article.insights.length > 0) {
        sentiment = getMostCommonSentiment(article.insights);
      }

      return {
        id: article.id || article.polygon_id || article.url, // try to keep key stable
        title: article.title || '',
        description: article.description || '',
        publishedDate: article.published_utc,
        author: article.author || '',
        publisher,
        publisherLogo: article.publisher_logo_url,
        publisherHomepage: article.publisher_homepage_url,
        publisherFavicon: article.publisher_favicon_url,
        url: article.article_url,
        imageUrl: article.image_url,
        stocks: Array.isArray(article.tickers) ? article.tickers : [],
        sentiment,
        sentimentScore: Number.isFinite(article.sentiment_score)
          ? Number(article.sentiment_score)
          : null,
        insights: Array.isArray(article.insights) ? article.insights : [],
        keywords,
        rawJson: article.raw_json,
      };
    });
  }, [sentimentData]);

  // Filter + sort newest first
  const filteredData = useMemo(() => {
    const base = selectedSentiment === 'all'
      ? transformedData
      : transformedData.filter((a) => a.sentiment === selectedSentiment);
    return [...base].sort(
      (a, b) =>
        (new Date(b.publishedDate).getTime() || 0) -
        (new Date(a.publishedDate).getTime() || 0)
    );
  }, [transformedData, selectedSentiment]);

  // Visible slice (3 by default)
  const visibleArticles = useMemo(
    () => (showAll ? filteredData : filteredData.slice(0, 3)),
    [filteredData, showAll]
  );

  // Counts for pills
  const sentimentCounts = useMemo(() => {
    const counts = transformedData.reduce((acc, a) => {
      acc[a.sentiment] = (acc[a.sentiment] || 0) + 1;
      return acc;
    }, {});
    return {
      all: transformedData.length,
      positive: counts.positive || 0,
      negative: counts.negative || 0,
      neutral: counts.neutral || 0,
    };
  }, [transformedData]);

  if (error) {
    return (
      <div className="h-full flex items-center justify-center bg-white dark:bg-slate-900/50 backdrop-blur-xl rounded-2xl border border-gray-200 dark:border-white/10">
        <div className="text-center space-y-4 p-8">
          <div className="w-16 h-16 mx-auto bg-rose-100 dark:bg-rose-500/10 backdrop-blur-sm rounded-full flex items-center justify-center border border-rose-500/20 shadow-lg shadow-rose-500/10">
            <Globe className="w-8 h-8 text-rose-700 dark:text-rose-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">{t('stocks.news.failedToLoad')}</h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm max-w-md">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-slate-900/50 backdrop-blur-xl rounded-2xl border border-gray-200 dark:border-white/10 overflow-hidden shadow-2xl shadow-black/20">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-white/10 bg-gradient-to-r from-gray-50 via-gray-100 to-transparent dark:from-white/5 dark:via-white/10 dark:to-transparent backdrop-blur-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-cyan-600 via-blue-600 to-violet-600 dark:from-cyan-400 dark:via-blue-400 dark:to-purple-400 bg-clip-text text-transparent mb-1">
              {t('stocks.news.title')}
            </h2>
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 text-sm">
              <Clock className="w-4 h-4" />
              <span>{t('stocks.news.subtitle')}</span>
              <span className="text-gray-400 dark:text-gray-600">•</span>
              <span className="text-cyan-700 dark:text-cyan-400 font-medium">{t('stocks.news.articlesCount', { count: filteredData.length })}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 dark:bg-white/5 backdrop-blur-sm rounded-full border border-gray-200 dark:border-white/10">
            <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse shadow-lg shadow-emerald-400/50"></div>
            <span className="text-xs text-gray-700 dark:text-gray-300 font-medium">{t('stocks.common.live')}</span>
          </div>
        </div>

        {/* Sentiment pills */}
        <div className="flex gap-2 flex-wrap">
          {[
            { key: 'all', label: t('stocks.news.filters.all'), count: sentimentCounts.all, gradient: 'from-purple-500/20 to-blue-500/20' },
            { key: 'positive', label: t('stocks.news.filters.positive'), count: sentimentCounts.positive, gradient: 'from-emerald-500/20 to-green-500/20' },
            { key: 'negative', label: t('stocks.news.filters.negative'), count: sentimentCounts.negative, gradient: 'from-rose-500/20 to-red-500/20' },
            { key: 'neutral', label: t('stocks.news.filters.neutral'), count: sentimentCounts.neutral, gradient: 'from-amber-500/20 to-yellow-500/20' },
          ].map(({ key, label, count, gradient }) => (
            <button
              key={key}
              onClick={() => {
                setSelectedSentiment(key);
                setShowAll(false); // reset to compact view when switching tabs
              }}
              className={`px-4 py-2 rounded-full text-xs font-medium transition-all duration-300 backdrop-blur-sm border relative overflow-hidden ${
                selectedSentiment === key
                  ? `bg-gradient-to-r ${gradient} border-white/30 text-white shadow-lg ring-2 ring-white/20`
                  : 'bg-gray-100 dark:bg-white/5 border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-white/20'
              }`}
            >
              <span className="relative z-10 flex items-center gap-2">
                {label}
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  selectedSentiment === key ? 'bg-white/20' : 'bg-gray-200 dark:bg-white/10'
                }`}>
                  {count}
                </span>
              </span>
              {selectedSentiment === key && (
                <div className={`absolute inset-0 bg-gradient-to-r ${gradient} opacity-20 animate-pulse`} />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900/20 dark:to-slate-800/20">
          <div className="text-center space-y-6">
            <div className="relative">
              <div className="w-16 h-16 mx-auto border-4 border-gray-200 dark:border-slate-700/20 border-t-cyan-400 rounded-full animate-spin shadow-lg shadow-cyan-400/20" />
              <div className="absolute inset-0 w-16 h-16 mx-auto border-4 border-transparent border-t-purple-400 rounded-full animate-spin animation-delay-150 shadow-lg shadow-purple-400/20" />
            </div>
            <div>
              <p className="text-gray-700 dark:text-gray-300 text-sm font-medium">{t('stocks.news.loading')}</p>
              <p className="text-gray-500 dark:text-gray-500 text-xs mt-1">{t('stocks.news.analyzingSentiment')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {!loading && (
        <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-track-slate-800/50 scrollbar-thumb-cyan-500/30 hover:scrollbar-thumb-cyan-400/50">
          <div className="p-6 space-y-4">
            {filteredData.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 mx-auto bg-gradient-to-br from-gray-100 to-gray-200 dark:from-slate-700/20 dark:to-slate-600/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6 border border-gray-200 dark:border-white/10">
                  <Globe className="w-10 h-10 text-gray-500 dark:text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-3">{t('stocks.news.noArticlesFound')}</h3>
                <p className="text-gray-500 text-sm max-w-md mx-auto">
                  {selectedSentiment === 'all'
                    ? t('stocks.news.noArticlesAll')
                    : t('stocks.news.noArticlesFiltered', { sentiment: selectedSentiment })}
                </p>
              </div>
            ) : (
              visibleArticles.map((article, index) => {
                const sentimentConfig = getSentimentConfig(article.sentiment);
                const key = article.id || article.url || index;
                return (
                  <article
                    key={key}
                    className={`group relative backdrop-blur-sm bg-gray-50 dark:bg-white/5 rounded-xl border border-gray-200 dark:border-white/10 overflow-hidden transition-all duration-500 hover:bg-gray-100 dark:hover:bg-white/10 hover:border-gray-300 dark:hover:border-white/20 hover:shadow-2xl ${sentimentConfig.glowColor} ${
                      hoveredCard === index ? 'scale-[1.02] z-10 ring-2 ring-white/20' : ''
                    }`}
                    onMouseEnter={() => setHoveredCard(index)}
                    onMouseLeave={() => setHoveredCard(null)}
                  >
                    {/* bg gradient */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${sentimentConfig.bgGradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                    <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none border border-white/20 shadow-inner" />

                    <div className="relative p-6">
                      {/* header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`p-2.5 rounded-xl bg-gray-100 dark:bg-white/5 backdrop-blur-sm border ${sentimentConfig.borderColor} shadow-lg ${sentimentConfig.glowColor}`}>
                            {sentimentConfig.icon}
                          </div>
                          <div>
                            <span className={`${sentimentConfig.textColor} text-xs font-bold tracking-wider uppercase`}>
                              {article.sentiment}
                              {Number.isFinite(article.sentimentScore) && (
                                <span className="ml-1 text-gray-500 dark:text-gray-400 font-normal">
                                  ({Number(article.sentimentScore).toFixed(2)})
                                </span>
                              )}
                            </span>
                            <div className="flex items-center gap-2 mt-1">
                              <div className="flex items-center gap-1">
                                {article.publisherFavicon && (
                                  <img
                                    src={article.publisherFavicon}
                                    alt=""
                                    className="w-3 h-3"
                                    onError={(e) => (e.currentTarget.style.display = 'none')}
                                  />
                                )}
                                <span className="text-gray-600 dark:text-gray-400 text-xs font-medium">{article.publisher}</span>
                              </div>
                              <span className="text-gray-400 dark:text-gray-600">•</span>
                              <time className="text-gray-500 dark:text-gray-400 text-xs">{formatDate(article.publishedDate)}</time>
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => article.url && window.open(article.url, '_blank')}
                          aria-label={`Open article: ${article.title || 'article'}`}
                          className="opacity-0 group-hover:opacity-100 transition-all duration-300 p-2.5 rounded-xl bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 text-cyan-700 dark:text-cyan-400 hover:text-cyan-800 dark:hover:text-cyan-300 backdrop-blur-sm border border-gray-200 dark:border-white/10 hover:border-cyan-400/30 shadow-lg hover:shadow-cyan-400/20"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </button>
                      </div>

                      {/* title + desc */}
                      {article.title && (
                        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3 line-clamp-2 group-hover:bg-gradient-to-r group-hover:from-cyan-200 group-hover:to-purple-200 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300 leading-relaxed">
                          {article.title}
                        </h3>
                      )}
                      {article.description && (
                        <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-4 line-clamp-2 group-hover:text-gray-800 dark:group-hover:text-gray-300 transition-colors duration-300">
                          {article.description}
                        </p>
                      )}

                      {/* tickers */}
                      {Array.isArray(article.stocks) && article.stocks.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {article.stocks.slice(0, 4).map((t) => (
                            <span
                              key={t}
                              className="px-3 py-1.5 bg-gradient-to-r from-gray-100 to-gray-50 dark:from-white/10 dark:to-white/5 rounded-lg text-gray-700 dark:text-gray-300 text-xs font-medium backdrop-blur-sm border border-gray-200 dark:border-white/20 hover:border-cyan-400/50 hover:shadow-lg hover:shadow-cyan-400/20 transition-all duration-300 hover:scale-105"
                            >
                              ${t}
                            </span>
                          ))}
                          {article.stocks.length > 4 && (
                            <span className="px-3 py-1.5 bg-gray-100 dark:bg-white/5 rounded-lg text-gray-500 text-xs border border-gray-200 dark:border-white/10 backdrop-blur-sm">
                              +{article.stocks.length - 4} more
                            </span>
                          )}
                        </div>
                      )}

                      {/* keywords */}
                      {Array.isArray(article.keywords) && article.keywords.length > 0 && (
                        <div className="flex items-center gap-2 mb-4">
                          <Tag className="w-3 h-3 text-gray-500 dark:text-gray-500" />
                          <div className="flex flex-wrap gap-1.5">
                            {article.keywords.slice(0, 3).map((kw) => (
                              <span
                                key={kw}
                                className="text-xs text-gray-500 bg-gray-100 dark:bg-white/5 backdrop-blur-sm px-2.5 py-1 rounded-full border border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/20 hover:text-gray-700 dark:hover:text-gray-400 transition-all duration-300"
                              >
                                {kw}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* footer */}
                      <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-white/10">
                        <div className="flex items-center gap-2 text-gray-500 dark:text-gray-500 text-xs">
                          <User className="w-3 h-3" />
                          <span className="truncate max-w-32 font-medium">{article.author || '—'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {article.insights.length > 0 && (
                            <span className="px-3 py-1 bg-gray-100 dark:bg-white/10 backdrop-blur-sm rounded-full text-xs text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-white/10 shadow-inner">
                              {article.insights.length} insight{article.insights.length !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })
            )}
          </div>

          {/* Show More / Less */}
          {filteredData.length > 3 && (
            <div className="p-4 border-t border-gray-200 dark:border-white/10 bg-gradient-to-r from-gray-50 via-gray-100 to-transparent dark:from-white/5 dark:via-white/10 dark:to-transparent">
              <button
                onClick={() => setShowAll((v) => !v)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-100 to-violet-100 dark:from-blue-500/20 dark:to-purple-500/20 hover:from-blue-200 dark:hover:from-blue-500/30 hover:to-violet-200 dark:hover:to-purple-500/30 rounded-xl border border-blue-300 dark:border-blue-400/30 text-blue-700 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200 transition-all duration-200"
              >
                <span className="font-medium">
                  {showAll ? 'Show Less' : `Show ${filteredData.length - 3} More`}
                </span>
                {showAll ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>
          )}

          {/* Optional pagination button (for server-side "load more pages") */}
          {onLoadMore && !loading && filteredData.length > 0 && showAll && (
            <div className="p-6 pt-0">
              <button
                onClick={onLoadMore}
                className="w-full backdrop-blur-sm bg-gradient-to-r from-gray-50 to-gray-100 dark:from-white/5 dark:to-white/10 hover:from-cyan-100 dark:hover:from-cyan-500/10 hover:to-violet-100 dark:hover:to-purple-500/10 rounded-xl border border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/20 px-6 py-4 text-gray-700 dark:text-gray-300 text-sm font-medium transition-all duration-300 hover:text-gray-900 dark:hover:text-white group relative overflow-hidden hover:shadow-2xl hover:shadow-cyan-500/10"
              >
                <div className="relative z-10 flex items-center justify-center gap-2">
                  <span>Load More Articles</span>
                  <ExternalLink className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </button>
            </div>
          )}
          {/* COMPLIANCE: Additional footer disclaimer */}
          <div className="text-xs text-gray-500 italic bg-gray-100 dark:bg-slate-900/40 border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2">
            <p>
              This display classifies linguistic tone only. Positive/negative language does not correlate with or predict market movements. 
              All opinions shown are historical user-generated content and do not constitute financial advice or recommendations.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SentimentNews;
